<script setup>
import { computed } from 'vue'

/**
 * InfoTip.vue
 *
 * A small, focusable icon button that shows a contextual tooltip — intended to
 * sit inline next to a form label as a non-invasive help hint. The trigger is a
 * compact PrimeVue Button (text + rounded) whose chrome is neutralized to a bare
 * icon with no min-width, min-height, or padding. The tooltip appears on both
 * hover and keyboard focus on the side specified by the `position` prop.
 *
 * The component performs NO internal i18n — all text must already be translated
 * by the caller.
 *
 * @example
 * <InfoTip :text="$t('settings.helpHint')" position="right" />
 * <InfoTip :text="$t('form.labelHelp')" icon="help" />
 *
 * @prop {String} text     - Already-translated tooltip content (and aria-label).
 * @prop {String} position - Which side to show the tooltip on: 'top' | 'bottom' |
 *                           'left' | 'right'. Defaults to 'top'.
 * @prop {String} icon     - Material Icon name to render inside the button.
 *                           Defaults to 'info'.
 */

const props = defineProps({
  /** Already-translated tooltip content (also used as aria-label). */
  text: {
    type: String,
    required: true
  },
  /**
   * Side on which the tooltip appears.
   * @values top, bottom, left, right
   */
  position: {
    type: String,
    default: 'top',
    validator: (v) => ['top', 'bottom', 'left', 'right'].includes(v)
  },
  /** Material Icon name for the trigger glyph. */
  icon: {
    type: String,
    default: 'info'
  }
})

/**
 * Computed directive argument for the tooltip position.
 * PrimeVue's Tooltip directive reads `binding.arg` in `getModifiers()`:
 *   if (key === 'position') acc[val] = true
 * Passing `{ position: 'top' }` as the directive arg is equivalent to the
 * static `.top` modifier but works reactively via computed.
 */
const tooltipArg = computed(() => ({ position: props.position }))
</script>

<template>
  <Button
    text
    rounded
    class="info-tip !h-5 !w-5 !min-h-0 !min-w-0 !p-0"
    v-tooltip:[tooltipArg]="text"
    :aria-label="text"
  >
    <span class="material-icons select-none !text-[16px]">{{ icon }}</span>
  </Button>
</template>

<style scoped>
/* Neutralize the Button's hover/focus tint to stay subtle inline.
   The icon inherits the surrounding text color (--p-text-muted-color)
   from the scoped rule below rather than the Button's own color token. */
.info-tip {
  color: var(--p-text-muted-color);
  vertical-align: middle;
}

.info-tip:enabled:hover,
.info-tip:enabled:focus-visible {
  background: transparent;
  color: var(--p-text-color);
}
</style>
